import { lazy, Suspense } from "react"
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom"
import { DashboardProvider } from "@/context-state/DashboardContext.jsx"
import { DashboardDataProvider } from "@/pages/dashboard/DashboardDataProvider.jsx"
import { AUTH_BASE_PATH, DASHBOARD_ROOT } from '@/consts/index.js'
import { MainLoader } from '@/components/loader/MainLoader.jsx'
import { SnapshotDataProvider } from '@/pages/public/snapshot/SnapshotDataProvider.jsx'
import AuthGuard from "@/guards/AuthGuard.jsx"
import DashboardLayout from "@/layouts/dashboard"
import AccountGuard from '@/guards/AccountGuard.jsx'
import SpaceDefault from '@/pages/space/SpaceDefault.jsx'
import AccountDefault from '@/pages/account/AccountDefault.jsx'
import AuthLayout from '@/layouts/auth/index.jsx'
import { CreativeAnalyticsProvider } from '@/context-state/CreativeAnalyticsContext.jsx'
import { ResearchProvider } from '@/context-state/ResearchContext.jsx'
import { ResearchDataProvider } from '@/pages/dashboard/research/ResearchDataProvider.jsx'
import { ExternalAdDataProvider } from '@/pages/public/external-ad/ExternalAdDataProvider.jsx'
import { PaymentProvider } from '@/context-state/PaymentContext.jsx'
import { FreeTierLockGuard } from '@/guards/FreeTierLockGuard.jsx'
import { PaymentDataProvider } from '@/pages/space/PaymentDataProvider.jsx'
import { ErrorBoundary } from 'react-error-boundary'
import { AppErrorBoundary, handleDynamicRouteError } from '@/components/app-error-boundry/AppErrorBoundary.jsx'
import SpaceGuard from '@/guards/SpaceGuard.jsx'
import { AccountSpacesDataProvider } from "@/pages/dashboard/settings/account/account-spaces/AccountSpacesDataProvider.jsx"
import { SpaceMembersDataProvider } from "@/pages/dashboard/settings/space/space-members/SpaceMembersDataProvider.jsx"
import { AccountMembersDataProvider } from "@/pages/dashboard/settings/account/account-members/AccountMembersDataProvider.jsx"
import { LibraryProvider } from "@/context-state/LibraryContext.jsx"
import { BrandDataProvider } from '@/pages/dashboard/research/brand/BrandDataProvider.jsx'
import { BoardDataProvider } from "@/pages/dashboard/research/board/BoardDataProvider.jsx"
import { BrandStrategySnapshopDataProvider } from "@/pages/public/brand-strategy-snapshot/BrandStrategySnapshopDataProvider.jsx"


const Loadable = (Component) => (props) => {
    return (
        <ErrorBoundary FallbackComponent={AppErrorBoundary} onError={handleDynamicRouteError}>
            <Suspense fallback={<MainLoader />}>
                <Component {...props} />
            </Suspense>
        </ErrorBoundary>
    )
}

// Auth
const SignInPage = Loadable(lazy(() => import('../pages/auth/SignInPage.jsx')))
const RegisterPage = Loadable(lazy(() => import('../pages/auth/RegisterPage.jsx')))
const VerifyAuthPage = Loadable(lazy(() => import('../pages/auth/VerifyAuthPage.jsx')))
const AcceptInvitationPage = Loadable(lazy(() => import('../pages/auth/AcceptInvitationPage.jsx')))
// Dashboard
const CreativePage = Loadable(lazy(() => import('../pages/dashboard/creative/CreativePage.jsx')))
const LibraryPage = Loadable(lazy(() => import('../pages/dashboard/library/LibraryPage.jsx')))
const FolderPage = Loadable(lazy(() => import('../pages/dashboard/library/FolderPage.jsx')))
const IntegrationsPage = Loadable(lazy(() => import('../pages/dashboard/integrations/IntegrationsPage.jsx')))
const ResearchPage = Loadable(lazy(() => import('../pages/dashboard/research/ResearchPage.jsx')))
const BrandPage = Loadable(lazy(() => import('../pages/dashboard/research/brand/BrandsPage.jsx')))
const InsightsPage = Loadable(lazy(() => import('../pages/dashboard/insights/InsightsPage.jsx')))
const VideoExplorerPage = Loadable(lazy(() => import('../pages/dashboard/creative/video-explorer/VideoExplorerPage.jsx')))
const ClipsLibraryPage = Loadable(lazy(() => import('../pages/dashboard/clips-library/ClipsLibraryPage.jsx')))
const BoardView = Loadable(lazy(() => import('../pages/dashboard/clips-library/BoardView.jsx')))
const CompetitorsPage = Loadable(lazy(() => import('../pages/dashboard/research/competitors/CompetitorsPage.jsx')))
const OnboardingPage = Loadable(lazy(() => import('../pages/dashboard/onboarding/OnboardingPage.jsx')))
const DiscoveryPage = Loadable(lazy(() => import('../pages/dashboard/research/Discovery.jsx')))
const BoardPage = Loadable(lazy(() => import('../pages/dashboard/research/board/BoardPage.jsx')))
const OverviewPage = Loadable(lazy(() => import('../pages/dashboard/overview/OverviewPage.jsx')))

// Dashboard -> Settings
const AccountGeneralPage = Loadable(lazy(() => import('../pages/dashboard/settings/account/AccountGeneralPage.jsx')))
const AccountDataConnectionsPage = Loadable(lazy(() => import('../pages/dashboard/settings/account/AccountDataConnectionsPage.jsx')))
const AccountSpacesPage = Loadable(lazy(() => import('../pages/dashboard/settings/account/account-spaces/AccountSpacesPage.jsx')))
const AccountMembersPage = Loadable(lazy(() => import('../pages/dashboard/settings/account/account-members/AccountMembersPage.jsx')))
const AccountBillingPage = Loadable(lazy(() => import('../pages/dashboard/settings/account/AccountBillingPage.jsx')))
const SpaceGeneralPage = Loadable(lazy(() => import('../pages/dashboard/settings/space/SpaceGeneralPage.jsx')))
const SpaceDataSourcesPage = Loadable(lazy(() => import('../pages/dashboard/settings/space/SpaceDataSourcesPage.jsx')))
const SpaceMembersPage = Loadable(lazy(() => import('../pages/dashboard/settings/space/space-members/SpaceMembersPage.jsx')))
const UserGeneralPage = Loadable(lazy(() => import('../pages/dashboard/settings/user/UserGeneralPage.jsx')))

// Connect
const KlaviyoConnect = Loadable(lazy(() => import('../pages/connect/Klaviyo.jsx')))
const GoogleAdsConnect = Loadable(lazy(() => import('../pages/connect/GoogleAds.jsx')))
const ShopifyConnect = Loadable(lazy(() => import('../pages/connect/shopify/Shopify.jsx')))
// Publicly Accessible Pages
const SnapshotPage = Loadable(lazy(() => import('../pages/public/snapshot/SnapshotPage.jsx')))
const ExternalAdPage = Loadable(lazy(() => import('../pages/public/external-ad/ExternalAdPage.jsx')))
const BrandStrategySnapshopPage = Loadable(lazy(() => import('../pages/public/brand-strategy-snapshot/BrandStrategySnapshopPage.jsx')))

// Public Payment & Verify Payments Pages
const PaddlePublicCheckoutPage = Loadable(lazy(() => import('../pages/payment/paddle/PaddlePublicCheckoutPage.jsx')))
const VerifyShopifyPaymentPage = Loadable(lazy(() => import('../pages/payment/shopify/VerifyShopifyPaymentPage.jsx')))


// TODO - continue the flow
//  1. CRUD for the spaces
//  2. Add / remove user to a space
//  3. Change user's role in a space

export const router = createBrowserRouter([
    {
        path: "/",
        element: <Navigate to={AUTH_BASE_PATH + (window.location.search || '')} />,
        index: true
    },
    {
        path: 'auth',
        element: (
            <AuthLayout />
        ),
        children: [
            {
                path: "login",
                element: <SignInPage />,
            },
            {
                path: 'accept-invitation',
                element: <AcceptInvitationPage />
            },
            {
                path: "register",
                element: <RegisterPage />,
            },
            {
                path: "verify-auth",
                element: <VerifyAuthPage />,
            }
        ]
    },
    {
        path: 'payment',
        children: [
            {
                element: <Navigate to={AUTH_BASE_PATH + (window.location.search || '')} />,
                index: true,
            },
            {
                path: 'paddle',
                element: <PaddlePublicCheckoutPage />
            },
            {
                path: 'shopify',
                element: <VerifyShopifyPaymentPage />
            }
        ]
    },
    {
        path: 'public',
        children: [
            {
                element: <Navigate to="/" />,
                index: true
            },
            {
                path: 'snapshot/:snapshotId',
                element: <SnapshotDataProvider><SnapshotPage /></SnapshotDataProvider>
            },
            {
                path: 'preview-ad/:externalAdId',
                element: <ExternalAdDataProvider><ExternalAdPage /></ExternalAdDataProvider>
            },
            {
                path: 'brand-strategy/:snapshotId',
                element: <BrandStrategySnapshopDataProvider><BrandStrategySnapshopPage /></BrandStrategySnapshopDataProvider>
            },
            {
                path: "*",
                element: <Navigate to="/" />
            },
        ]
    },
    {
        path: 'connect',
        element: (
            // <AuthGuard>
            // <div className="h-full bg-main-surface bg-center bg-cover bg-no-repeat overflow-hidden relative">
            <Outlet />
            // </div>

            // </AuthGuard>
        ),
        children: [
            {
                path: "shopify",
                element: <ShopifyConnect />
            },
            {
                path: "klaviyo",
                element: <KlaviyoConnect />
            },
            {
                path: "google-ads",
                element: <GoogleAdsConnect />
            }
        ]
    },
    {
        path: 'account',
        element: (
            <AuthGuard>
                <PaymentProvider>
                    <AccountDefault />
                </PaymentProvider>
            </AuthGuard>
        ),
        children: [
            {
                path: ':accountId',
                element: <AccountGuard />,
                children: [
                    {
                        path: 'space',
                        element: <SpaceDefault />,
                        children: [
                            {
                                path: ':spaceId',
                                element: <SpaceGuard />,
                                children: [
                                    {
                                        element: <Navigate to="dashboard" replace />,
                                        index: true
                                    },
                                    {
                                        path: "dashboard",
                                        element: (
                                            <PaymentDataProvider>
                                                <FreeTierLockGuard>
                                                    <DashboardProvider>
                                                        <DashboardDataProvider>
                                                            <CreativeAnalyticsProvider>
                                                                <ResearchProvider>
                                                                    <LibraryProvider>
                                                                        <DashboardLayout />
                                                                    </LibraryProvider>
                                                                </ResearchProvider>
                                                            </CreativeAnalyticsProvider>
                                                        </DashboardDataProvider>
                                                    </DashboardProvider>
                                                </FreeTierLockGuard>
                                            </PaymentDataProvider>
                                        ),
                                        children: [
                                            {
                                                element: <Navigate to={DASHBOARD_ROOT} replace />,
                                                index: true
                                            },
                                            {
                                                path: "overview",
                                                element: <OverviewPage />
                                            },
                                            {
                                                path: "onboarding",
                                                element: <OnboardingPage />
                                            },
                                            {
                                                path: "creative",
                                                children: [
                                                    {
                                                        index: true,
                                                        element: <CreativePage />
                                                    },
                                                    {
                                                        path: "insights",
                                                        element: <InsightsPage />
                                                    },
                                                    {
                                                        path: "video-explorer",
                                                        element: <VideoExplorerPage />
                                                    }
                                                ]

                                            },
                                            {
                                                path: "research",
                                                element: <ResearchDataProvider><Outlet /></ResearchDataProvider>,
                                                children: [
                                                    {
                                                        index: true,
                                                        element: <ResearchPage />
                                                    },
                                                    // {
                                                    //     path: "discovery",
                                                    //     element: <DiscoveryPage />
                                                    // },
                                                    {
                                                        path: "competitors",
                                                        element: <CompetitorsPage />
                                                    },
                                                    {
                                                        path: 'board/:boardId',
                                                        element: <BoardDataProvider><BoardPage /></BoardDataProvider>
                                                    },
                                                    {
                                                        path: ":advertiserId",
                                                        element: <BrandDataProvider><BrandPage /></BrandDataProvider>
                                                    },
                                                ]

                                            },
                                            {
                                                path: "library",
                                                children: [
                                                    {
                                                        index: true,
                                                        element: <LibraryPage />
                                                    },
                                                    {
                                                        path: ":folderId",
                                                        element: <FolderPage />
                                                    }
                                                ]
                                            },
                                            {
                                                path: "clips-library",
                                                children: [
                                                    {
                                                        index: true,
                                                        element: <ClipsLibraryPage />
                                                    },
                                                    {
                                                        path: 'board/:boardId',
                                                        element: <BoardView />
                                                    }
                                                ]
                                            },
                                            {
                                                path: "integrations",
                                                children: [
                                                    {
                                                        index: true,
                                                        // element: <IntegrationsPage />,
                                                    },
                                                    {
                                                        path: "connect-klaviyo",
                                                        element: <KlaviyoConnect />
                                                    }
                                                ]
                                            },
                                            {
                                                path: "settings",
                                                children: [
                                                    {
                                                        path: '',
                                                        element: <Navigate to="account/general" replace />
                                                    },
                                                    {
                                                        path: 'account',
                                                        children: [
                                                            {
                                                                path: '',
                                                                element: <Navigate to="general" replace />
                                                            },
                                                            {
                                                                path: 'general',
                                                                element: <AccountGeneralPage />
                                                            },
                                                            {
                                                                path: 'spaces',
                                                                element: <AccountSpacesDataProvider><AccountSpacesPage /></AccountSpacesDataProvider>
                                                            },
                                                            {
                                                                path: 'data-connections',
                                                                element: <AccountDataConnectionsPage />
                                                            },
                                                            {
                                                                path: 'members',
                                                                element: <AccountMembersDataProvider><AccountMembersPage /></AccountMembersDataProvider>
                                                            },
                                                            {
                                                                path: 'billing',
                                                                element: <AccountBillingPage />
                                                            }
                                                        ]
                                                    },
                                                    {
                                                        path: 'space',
                                                        children: [
                                                            {
                                                                path: '',
                                                                element: <Navigate to="details" replace />
                                                            },
                                                            {
                                                                path: 'general',
                                                                element: <SpaceGeneralPage />
                                                            },
                                                            {
                                                                path: 'data-sources',
                                                                element: <SpaceDataSourcesPage />
                                                            },
                                                            {
                                                                path: 'members',
                                                                element: <SpaceMembersDataProvider><SpaceMembersPage /></SpaceMembersDataProvider>
                                                            }
                                                        ]
                                                    },
                                                    {
                                                        path: 'user',
                                                        children: [
                                                            {
                                                                path: '',
                                                                element: <Navigate to="details" replace />
                                                            },
                                                            {
                                                                path: 'details',
                                                                element: <UserGeneralPage />
                                                            }
                                                        ]
                                                    }
                                                ]
                                            },
                                            {
                                                path: "*",
                                                element: <Navigate to={DASHBOARD_ROOT} />
                                            },
                                        ]
                                    },
                                ]
                            }
                        ]
                    }
                ]
            },
        ]
    },
    {
        path: "*",
        element: <Navigate to={AUTH_BASE_PATH + (window.location.search || '')} />
    },
])
